<template>
  <div class="p-4 rounded-lg border border-gray-600 dark:border-gray-700">
    <SimpleSkeleton
      v-if="consultandoVistaPrevia"
      :options="getSkeleton(1, 5, { anchoC: 'width:100%', anchoF: 'width:100%', altoF: 'h-10' })"
    />
    <div
      v-else-if="editable"
    >
      <div class="mb-3 text-sm font-semibold uppercase text-gray-800 dark:text-gray-200">
        {{ tituloVistaPrevia }}
      </div>
      <div class="rounded-lg border border-gray-400 dark:border-gray-700">
        <p class="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          {{ mensajeVistaPreviaDisenador }}
        </p>
      </div>
    </div>
    <div
      v-else-if="!puedeConsultar"
      class="py-4 text-sm text-gray-500 dark:text-gray-400"
    >
      {{ mensajeNoDisponible }}
    </div>
    <div
      v-else-if="errorVistaPrevia"
      class="p-4 text-center"
    >
      <p class="mb-3 text-sm text-red-600 dark:text-red-400">
        {{ errorVistaPrevia }}
      </p>
      <ButtonDefault color="blue" @click="cargarVistaPrevia">
        Reintentar
      </ButtonDefault>
    </div>
    <template v-else-if="resultadoVistaPrevia">
      <div class="mb-3 text-sm font-semibold uppercase text-gray-800 dark:text-gray-200">
        {{ resultadoVistaPrevia.titulo }}
      </div>
      <ListaSqlTabla
        :resultado="resultadoVistaPrevia"
        :identification="`lista-sql-preview-${comp.name}`"
      />
      <div
        v-if="hayMas"
        class="flex items-center justify-end mt-1"
      >
        <button
          type="button"
          class="cursor-pointer text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50 disabled:pointer-events-none"
          :disabled="consultandoDetalle"
          @click="verMas"
        >
          Ver más
        </button>
      </div>
    </template>
  </div>
  <ListaSqlModal
    ref="modalRef"
    :modal-id="`lista-sql-${comp.name}`"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { IFormComponent } from '@/modules/Configuracion/interfaces/IFormComponent'
import { useListaSql } from '@/modules/Configuracion/composables/useListaSql'
import { obtenerTituloFallbackListaSql } from '@/modules/Configuracion/utils/listaSql'
import { getSkeleton } from '@/modules/Core/usings/generales'
import ListaSqlModal from '@/modules/Core/components/ListaSql/ListaSqlModal.vue'
import ListaSqlTabla from '@/modules/Core/components/ListaSql/ListaSqlTabla.vue'
import SimpleSkeleton from '@/modules/Core/components/general/SimpleSkeleton.vue'

const props = defineProps<{
  comp: IFormComponent
  cittId?: number
  editable?: boolean
}>()

const modalRef = ref<InstanceType<typeof ListaSqlModal> | null>(null)
const cargaVistaPrevia = useListaSql()
const cargaDetalle = useListaSql()
const {
  consultando: consultandoVistaPrevia,
  error: errorVistaPrevia,
  resultado: resultadoVistaPrevia,
  hayMas
} = cargaVistaPrevia
const { consultando: consultandoDetalle } = cargaDetalle

const puedeConsultar = computed(() => {
  return !props.editable && !!props.cittId && !!props.comp.properties?.coreId
})
const tituloVistaPrevia = computed(() => props.comp.label || 'Consulta SQL')
const mensajeVistaPreviaDisenador = computed(() => {
  if (!props.comp.properties?.coreId) {
    return 'Selecciona un reporte para visualizar sus resultados durante la atención.'
  }
  return 'Los resultados de este reporte se visualizarán aquí durante la atención.'
})
const mensajeNoDisponible = computed(() => {
  if (!props.comp.properties?.coreId) return 'Debe configurar un reporte para cargar la vista previa.'
  return 'Disponible únicamente dentro de una atención.'
})

const obtenerParametros = () => {
  const coreId = props.comp.properties?.coreId
  if (!coreId || !props.cittId) return null

  return {
    coreId,
    cittId: props.cittId,
    label: obtenerTituloFallbackListaSql(props.comp)
  }
}

const cargarVistaPrevia = async () => {
  const parametros = obtenerParametros()
  if (!puedeConsultar.value || !parametros) return

  try {
    await cargaVistaPrevia.cargarVistaPreviaTabla({
      ...parametros,
      pagina: 1,
      limite: 5
    })
  } catch (error) {
    console.error('Error cargando la vista previa del reporte:', error)
  }
}

const verMas = async () => {
  const parametros = obtenerParametros()
  if (!parametros || !modalRef.value) return

  window.notificacion.loading().circle('Consultando información...')
  try {
    const resultado = await cargaDetalle.cargarResultadoTabla(parametros)
    await modalRef.value.open(resultado)
  } catch (error) {
    console.error('Error consultando el detalle del reporte:', error)
    window.notificacion.notificacion(
      'failure',
      'No fue posible consultar la información. Intenta nuevamente.'
    )
  } finally {
    window.notificacion.loading().remove()
  }
}

watch(
  () => [props.comp.properties?.coreId, props.cittId, props.editable],
  () => {
    void cargarVistaPrevia()
  },
  { immediate: true }
)
</script>
